import React, { useState } from 'react';
import { InputGroup, FormControl, Spinner, Modal, Tabs, Tab } from 'react-bootstrap';
import { generateContent, generateContentFromPrompt, generateFocusKeyphrase } from '../api/content';
import { updateFocusKeyphrase, updateSlug, updateTitle, updateMeta, duplicatePost, updateContent } from '../api/wordpress';
import { generateSlug } from '../utils';
import { ContentResponse, NeedleInput } from '../types';
import SingleLandingPageGenerator from '../components/SingleLandingPageGenerator';
import BulkLandingPageGenerator from '../components/BulkLandingPageGenerator';

const LandingPageGenerator: React.FC = () => {
  const [industries, setIndustries] = useState<string[]>([
    "Consumer products",
    "Sport & Leisure",
    "Professional Services",
    "Public Sector & Non-Profit",
    "Food & Beverage",
    "Finance",
    "Health & Medical",
    "Manufacturing",
    "Construction",
    "Art & Design",
    "Education"
  ]);

  const [services, setServices] = useState<string[]>([
    "Web Development",
    "Mobile Development",
    "Ecommerce",
    "Subscription Services",
    "Technical Staff Augmentation",
    "Hosting, Cloud and Data",
    "Outsourced Technical Customer Support"
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [originalPostId, setOriginalPostId] = useState<number>(1100);
  const [needleInputs, setNeedleInputs] = useState<NeedleInput[]>([
    {
      needle: "Business Process Automation",
      includeKeyPhrase: true,
      type: "H1",
      wordCount: 5,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Streamline Your Operations with Business Process Automation",
      includeKeyPhrase: true,
      type: "H2",
      wordCount: 7,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Unlock Efficiency and Agility through Automated Workflows and Enhanced Customer Experiences",
      includeKeyPhrase: false,
      type: "H3",
      wordCount: 9,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "What is BPA?",
      includeKeyPhrase: false,
      type: "Feature box 1 title",
      wordCount: 3,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Discover how BPA automates and improves specific business functions or workflows",
      includeKeyPhrase: false,
      type: "Feature box 1 content",
      wordCount: 20,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Automating Your Processes",
      includeKeyPhrase: false,
      type: "Feature box 2 title",
      wordCount: 3,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Learn why businesses should consider process automation to achieve greater efficiency",
      includeKeyPhrase: false,
      type: "Feature box 2 content",
      wordCount: 20,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Harnessing the Power of RPA",
      includeKeyPhrase: false,
      type: "Feature box 3 title",
      wordCount: 3,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "Explore how Robotic Process Automation (RPA) enables seamless integration with legacy systems",
      includeKeyPhrase: true,
      type: "Feature box 3 content",
      wordCount: 20,
      paragraphCount: 1,
      content: ""
    },
    {
      needle: "At Gemstone, we possess a strong track record in implementing effective business process automation solutions",
      includeKeyPhrase: true,
      type: "Skills and expertise section",
      wordCount: 110,
      paragraphCount: 3,
      content: ""
    }
  ]);

  const addNeedleInput = () => {
    const defaultNeedle: NeedleInput = {
      needle: '',
      includeKeyPhrase: false,
      type: '',
      wordCount: 0
    };
    setNeedleInputs(prevNeedles => [...prevNeedles, defaultNeedle]);
  };

  const updateNeedleValue = (index: number, value: string) => {
    setNeedleInputs(prevNeedles => {
      const updatedNeedles = [...prevNeedles];
      updatedNeedles[index] = { ...updatedNeedles[index], needle: value };
      return updatedNeedles;
    });
  };

  const addStatusMessage = (message: string) => {
    setStatusMessages(prevStatus => [...prevStatus, message]);
  };


  const handleGenerate = async () => {

    setIsLoading(true);

    try {
      for (let industry of industries) {
        for (let service of services) {
          addStatusMessage(`Generating for Industry: ${industry} & Service: ${service}`);

          const newPostId = await duplicatePost(originalPostId);
          if (newPostId === undefined) {
            addStatusMessage(`Failed to duplicate post for Industry: ${industry} & Service: ${service}. Aborting.`);
            return; // Exit if duplication failed
          }
          addStatusMessage(`Duplicated post successfully for Industry: ${industry} & Service: ${service}. New Post ID: ${newPostId}`);

          const generatedTitle = await generateContentFromPrompt(`Provide an SEO friendly title for a page advertising the ${service} services we provide to customers in the ${industry} industry. Use British spelling but don't actually mention the UK.`);
          if (generatedTitle) {
            await updateTitle(generatedTitle, newPostId);
            addStatusMessage(`Generated title: ${generatedTitle}`);
          } else {
            addStatusMessage(`Failed to generate title for Post ID: ${newPostId}`);
          }

          const generatedKeyPhrase = await generateFocusKeyphrase(generatedTitle ?? '');
          if (generatedKeyPhrase) {
            await updateFocusKeyphrase(generatedKeyPhrase, newPostId);
            addStatusMessage(`Updated focus keyphrase: ${generatedKeyPhrase}`);
          }

          const generatedMetaDescription = await generateContentFromPrompt(`Provide an SEO friendly meta description for a page aimed at people looking for ${service} in the ${industry} industry. Include the exact phrase '${generatedKeyPhrase}' in the meta description. It MUST be less than 140 characters. Use British spelling but don't actually mention the UK.`);
          if (generatedMetaDescription) {
            await updateMeta(generatedMetaDescription, newPostId);
            addStatusMessage(`Generated meta description: ${generatedMetaDescription}`);
          } else {
            addStatusMessage(`Failed to generate meta description for Post ID: ${newPostId}`);
          }

          if (generatedTitle) {
            const slug = generateSlug(generatedTitle);
            await updateSlug(slug, newPostId);
            addStatusMessage(`Updated slug: ${slug}`);
          }

          let tempNeedleInputs = JSON.parse(JSON.stringify(needleInputs));

          console.log(tempNeedleInputs);

          // Loop through the needles and generate content for each one
          for (const needleInput of tempNeedleInputs) {
            const { needle, wordCount, paragraphCount, type } = needleInput;

            addStatusMessage(`Generating content based on ${needle}...`);
            const generatedContent: ContentResponse | undefined = await generateContent(tempNeedleInputs, newPostId, generatedTitle ?? '', generatedKeyPhrase ?? '', wordCount, paragraphCount ?? null, type);

            if (generatedContent) {
              // Update the content for the current needle in the needleInputs state
              const updatedNeedles = [...tempNeedleInputs];  // clone the current state
              const needleIndex = updatedNeedles.findIndex(n => n.needle === needle);

              if (needleIndex !== -1) {
                updatedNeedles[needleIndex].content = generatedContent.content;
                tempNeedleInputs  = updatedNeedles;
              }

              addStatusMessage(`Generating content based on ${needle}... Success!`);
              addStatusMessage(`Updating post ID: ${newPostId}...`);

              try {
                await updateContent(needle, generatedContent.content, newPostId);
                addStatusMessage(`Updating post ID: ${newPostId}... Success!`);
              } catch (error) {
                console.error(error);
                addStatusMessage(`Updating post ID: ${newPostId}... Failed!`);
                break;
              }
            } else {
              addStatusMessage(`Generating content based on ${needle}... Failed!`);
            }
          }

          addStatusMessage("Complete!");

        }
      }

      addStatusMessage("Bulk generation complete!");
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        addStatusMessage(error.message);
      } else {
        addStatusMessage('An unknown error occurred.');
      }
    }

    setIsLoading(false);
  };
  const LoadingModal: React.FC<{ messages: string[] }> = ({ messages }) => {
    return (
      <Modal show={isLoading} onHide={() => { }} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Loading...</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <Spinner animation="border" className="mb-3" />
          </div>
          {messages.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      <div className="container">
        <InputGroup className="mb-3">
          <InputGroup.Text>Original Post ID</InputGroup.Text>
          <FormControl
            type="number"
            placeholder="Enter original post ID"
            value={originalPostId}
            onChange={(e) => setOriginalPostId(Number(e.target.value))}
          />
        </InputGroup>
        <Tabs defaultActiveKey="bulk" id="landing-page-generator-tabs">
          <Tab eventKey="bulk" title="Bulk Landing Page Generator">
            <BulkLandingPageGenerator
              handleGenerate={handleGenerate}
              LoadingModal={LoadingModal}
              industries={industries}
              services={services}
              setIndustries={setIndustries}
              setServices={setServices}
              statusMessages={statusMessages}
            />
          </Tab>
          <Tab eventKey="single" title="Single Landing Page Generator">
            <SingleLandingPageGenerator
              needleInputs={needleInputs}
              updateNeedleValue={updateNeedleValue}
              addNeedleInput={addNeedleInput}
              addStatusMessage={addStatusMessage}
              setIsLoading={setIsLoading}
              originalPostId={originalPostId}
              setStatusMessages={setStatusMessages}
            />
          </Tab>
        </Tabs>
      </div>
    </>
  );
};

export default LandingPageGenerator;
